import { useEffect } from 'react';
import Router from 'next/router';
import { i18n } from 'next-i18next';

import { captureException } from '@restworld/utils-common';

/**
 * Recover from — and count — a client-side navigation that rendered a page with raw
 * translation keys.
 *
 * How it happens: `V2Link` / `useV2Router` send production navigations to `/v2/...`. For a
 * static page the router then fetches `/_next/data/<build>/it/v2/....json`, and production
 * answers that URL with the page's HTML (200, `text/html`) instead of its JSON. The router
 * does not treat that as a failure: it parses nothing and hands the page `pageProps = {}`.
 * Without `_nextI18Next`, next-i18next keeps only the namespaces already in memory, so every
 * key of a namespace no earlier page shipped renders raw (`hey_key`, `open_a_new_search`).
 * Measured on production 02/10/2026, session a1218454-c569-4686-84bc-ec893da436a9: the replay
 * shows "Ciao" on a hard load of the homepage and `hey_key` after a client navigation to
 * `/v2/employer/homepage`.
 *
 * Empty props are the normal outcome of every `/v2` client navigation, and most of them look
 * fine because the namespaces were loaded earlier. So this acts, and reports, only when a
 * namespace the page needs is actually missing from the i18n store:
 *
 * - The same data URL without `/v2` does return JSON. It is fetched once per route per tab,
 *   which yields the page's namespaces; any missing one is added to the live i18n instance
 *   and the page re-renders translated, without a reload.
 * - The page may have lost other props as well (`id`, `slug`, ...). They are not restored:
 *   that is how those pages already run after a `/v2` navigation, and they work. They are
 *   recorded as `lostProps`, so O2 can show whether that ever matters.
 *
 * Every repair is reported with a stable `error_type`, so O2 can count and alert on it.
 *
 * Since 11/2026 the shared translation guard (`translationGuardConfig` in `@restworld/utils-common`,
 * wired in `_app.tsx`) repairs the same pages too, from the render itself rather than from
 * `routeChangeComplete`, and often first. This hook stays as a second line and for `lostProps`;
 * when it reports, the guard has not finished. Counts of readers who saw raw keys come from the
 * guard's `i18n_missing_key`, which covers both apps and every cause.
 *
 * This is a deliberate, narrow exception to the app rule "don't fetch translations
 * client-side for SSR pages" (apps/employer-app/CLAUDE.md). It only runs when the router
 * already failed to deliver the server-side translations, and it re-uses the page's own
 * server-generated data JSON instead of a separate client-side i18n backend. It goes away
 * together with the `/v2` prefix.
 */
export const MISSING_PAGE_TRANSLATIONS_KIND = 'i18n_page_translations_missing';

/** What happened; lands in RUM as `context_recovery`. */
export type Recovery = 'in_place' | 'none_fetch_failed';

/** The fields of the router's per-route cache entry this check reads. */
export interface RouteCacheEntry {
  __N_SSG?: boolean;
  __N_SSP?: boolean;
  props?: { pageProps?: Record<string, unknown> };
}

/** `locale -> namespace -> resources`, as serialized by `serverSideTranslations`. */
export type I18nStore = Record<string, Record<string, Record<string, unknown>>>;

/**
 * True when a page that fetches its props on the server arrived without the i18n payload.
 * Pages without `getStaticProps` / `getServerSideProps` never carry one, so they are not
 * flagged: in this app every page that does fetch props also ships its translations.
 */
export function isMissingPageTranslations(entry: RouteCacheEntry | undefined): boolean {
  if (!entry || !(entry.__N_SSG || entry.__N_SSP)) return false;
  return !entry.props?.pageProps?._nextI18Next;
}

/**
 * The data URL the router should have fetched: the page's path with the `/v2` prefix
 * removed. `asPath` carries no locale prefix, so the locale is added back explicitly.
 */
export function buildDataHref(buildId: string, locale: string, asPath: string): string {
  const pathname = asPath.split(/[?#]/)[0];
  const withoutV2 = pathname.replace(/^\/v2(?=\/|$)/, '') || '/';
  const page = withoutV2 === '/' ? '/index' : withoutV2.replace(/\/$/, '');
  return `/_next/data/${buildId}/${locale}${page}.json`;
}

/** The `locale:namespace` pairs of `store` that `hasBundle` does not know about. */
export function findMissingBundles(store: I18nStore, hasBundle: (lng: string, ns: string) => boolean): string[] {
  return Object.entries(store).flatMap(([lng, namespaces]) =>
    Object.keys(namespaces)
      .filter((ns) => !hasBundle(lng, ns))
      .map((ns) => `${lng}:${ns}`)
  );
}

async function fetchPageProps(dataHref: string): Promise<Record<string, unknown> | null> {
  try {
    const response = await fetch(dataHref, { credentials: 'same-origin', headers: { 'x-nextjs-data': '1' } });
    if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) return null;
    const body = (await response.json()) as { pageProps?: Record<string, unknown> };
    return body.pageProps ?? null;
  } catch {
    return null;
  }
}

/**
 * The translations each route needs, fetched once per tab. Shallow navigations reuse the
 * same (still empty) router cache entry, so without this every one of them would refetch.
 */
const storeByRoute = new Map<string, I18nStore>();

interface RouterInternals {
  route: string;
  asPath: string;
  locale?: string;
  defaultLocale?: string;
  components?: Record<string, RouteCacheEntry>;
}

export async function recoverMissingPageTranslations(url: string): Promise<void> {
  // `components` is the router's internal route cache, and the only place that records
  // whether the page is static/server-rendered. If a Next upgrade removes it, the check
  // silently stops firing rather than misfiring, which is the safe direction.
  const router = Router.router as unknown as RouterInternals | null;
  const entry = router?.components?.[router.route];
  if (!router || !i18n || !isMissingPageTranslations(entry)) return;

  const { route, asPath } = router;
  const hasBundle = (lng: string, ns: string) => !!i18n?.hasResourceBundle(lng, ns);

  const known = storeByRoute.get(route);
  // Already fetched for this route and nothing is missing: the page renders translated.
  if (known && findMissingBundles(known, hasBundle).length === 0) return;

  let store = known;
  let lostProps: string[] | null = null;
  if (!store) {
    const buildId = window.__NEXT_DATA__?.buildId;
    const locale = router.locale ?? router.defaultLocale ?? 'it';
    const fullPageProps = buildId ? await fetchPageProps(buildDataHref(buildId, locale, asPath)) : null;
    store = (fullPageProps?._nextI18Next as { initialI18nStore?: I18nStore } | undefined)?.initialI18nStore;
    lostProps = fullPageProps ? Object.keys(fullPageProps).filter((key) => key !== '_nextI18Next') : null;
    if (store) storeByRoute.set(route, store);
  }

  if (!store) {
    // Typically a tab opened before the last deploy: the data URL carries the old build id and the
    // current deployment answers 404. No reload here: the translation guard has already fetched the
    // namespaces from the tab's own deployment (or reloads the page itself when it could not), so
    // reloading here would throw away a page that is already translated.
    captureException(new Error(`Could not load the translations of ${url}`), {
      kind: MISSING_PAGE_TRANSLATIONS_KIND,
      route,
      url,
      recovery: 'none_fetch_failed' satisfies Recovery,
    });
    return;
  }

  const missing = findMissingBundles(store, hasBundle);
  if (missing.length === 0) return;

  for (const [lng, namespaces] of Object.entries(store)) {
    for (const [ns, resources] of Object.entries(namespaces)) {
      if (!i18n.hasResourceBundle(lng, ns)) i18n.addResourceBundle(lng, ns, resources, true, true);
    }
  }
  // react-i18next re-renders on `languageChanged` only (its default `bindI18n`), and i18next
  // emits it even when the language does not change, which is what makes the new keys show.
  void i18n.changeLanguage(i18n.language);

  captureException(new Error(`Page rendered without its translations after navigating to ${url}`), {
    kind: MISSING_PAGE_TRANSLATIONS_KIND,
    route,
    url,
    recovery: 'in_place' satisfies Recovery,
    missingNamespaces: missing,
    lostProps,
  });
}

export function useRecoverMissingPageTranslations(): void {
  useEffect(() => {
    const onRouteChangeComplete = (url: string) => {
      void recoverMissingPageTranslations(url);
    };

    Router.events.on('routeChangeComplete', onRouteChangeComplete);
    return () => {
      Router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, []);
}
